import { useState } from 'react';
import type { SheetCellFormat } from '@itsappy/shared';
import { cn } from '@/lib/utils';
import { Dropdown, MenuItem } from '@/components/ui';
import {
  AddColumnsIcon,
  AddRowsIcon,
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  ClearFormatIcon,
  CopyIcon,
  FillColorIcon,
  FormulaIcon,
  ItalicIcon,
  TextColorIcon,
  TrashIcon,
} from '@/components/ui/icons';
import { ToolbarButton } from '@/components/doc/DocToolbar';

/**
 * The tools above a spreadsheet.
 *
 * Every format here was already in the cell model and already drawn by the
 * grid and written by the `.xlsx` export — there was simply no way to ask for
 * any of it. So this adds no new concept: it is the missing half of a feature
 * that was otherwise only reachable by importing a file that happened to have
 * the formatting in it.
 *
 * Formats apply to the whole selected range rather than one cell, because a
 * header row and a column of money are the two things anyone actually
 * formats, and both are ranges.
 */

export interface SheetToolbarProps {
  /** The A1 range the tools will act on, for the label. */
  range: string;
  /** The format of the anchor cell, for the pressed states. */
  format?: SheetCellFormat;
  /** What the anchor cell holds, so the formula button can say what it does. */
  canEdit: boolean;
  onFormat: (changes: Partial<SheetCellFormat>) => void;
  onClearFormat: () => void;
  /** Writes a total under or beside the selected numbers. */
  onSum: () => void;
  onCopy: () => void;
  onClear: () => void;
  onGrow: (what: 'rows' | 'cols') => void;
  className?: string;
}

/**
 * The fills and text colours on offer.
 *
 * A short list rather than a colour picker: these read on both themes, and a
 * free picker in a shared document reliably produces one cell of unreadable
 * yellow-on-white that somebody else then has to find and undo.
 */
const FILLS: { label: string; value?: string }[] = [
  { label: 'None', value: undefined },
  { label: 'Grey', value: '#e2e8f0' },
  { label: 'Yellow', value: '#fef3c7' },
  { label: 'Green', value: '#d1fae5' },
  { label: 'Blue', value: '#dbeafe' },
  { label: 'Red', value: '#fee2e2' },
];

const COLORS: { label: string; value?: string }[] = [
  { label: 'Default', value: undefined },
  { label: 'Grey', value: '#64748b' },
  { label: 'Green', value: '#047857' },
  { label: 'Red', value: '#b91c1c' },
  { label: 'Blue', value: '#1d4ed8' },
];

/** Decimal places offered, plus "as typed" for text and dates. */
const DECIMALS: { label: string; value?: number }[] = [
  { label: 'As typed', value: undefined },
  { label: '1,235', value: 0 },
  { label: '1,234.6', value: 1 },
  { label: '1,234.57', value: 2 },
];

const Divider = () => <span aria-hidden className="mx-0.5 h-5 w-px bg-surface-3" />;

/** A swatch, so a colour menu shows the colour rather than describing it. */
function Swatch({ color }: { color?: string }) {
  return (
    <span
      aria-hidden
      className="h-3.5 w-3.5 rounded border border-surface-4"
      style={{ background: color ?? 'transparent' }}
    />
  );
}

export function SheetToolbar({
  range,
  format,
  canEdit,
  onFormat,
  onClearFormat,
  onSum,
  onCopy,
  onClear,
  onGrow,
  className,
}: SheetToolbarProps) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    onCopy();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div
      role="toolbar"
      aria-label="Spreadsheet tools"
      className={cn(
        'flex flex-wrap items-center gap-0.5 border-b border-surface-3 bg-surface-2 px-2 py-1.5',
        className,
      )}
    >
      <span className="mr-1 min-w-16 font-mono text-[11px] text-slate-500">{range}</span>

      <ToolbarButton
        label="Bold"
        pressed={format?.bold}
        disabled={!canEdit}
        onClick={() => onFormat({ bold: !format?.bold })}
      >
        <BoldIcon size="sm" />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        pressed={format?.italic}
        disabled={!canEdit}
        onClick={() => onFormat({ italic: !format?.italic })}
      >
        <ItalicIcon size="sm" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="Align left"
        pressed={format?.align === 'left'}
        disabled={!canEdit}
        onClick={() => onFormat({ align: 'left' })}
      >
        <AlignLeftIcon size="sm" />
      </ToolbarButton>
      <ToolbarButton
        label="Align centre"
        pressed={format?.align === 'center'}
        disabled={!canEdit}
        onClick={() => onFormat({ align: 'center' })}
      >
        <AlignCenterIcon size="sm" />
      </ToolbarButton>
      <ToolbarButton
        label="Align right"
        pressed={format?.align === 'right'}
        disabled={!canEdit}
        onClick={() => onFormat({ align: 'right' })}
      >
        <AlignRightIcon size="sm" />
      </ToolbarButton>

      <Divider />

      <Dropdown
        label="Number format"
        trigger={
          <span className="grid h-7 min-w-7 place-items-center rounded-md px-1.5 text-[11px] font-medium text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink">
            .00
          </span>
        }
      >
        {(close) =>
          DECIMALS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => {
                close();
                onFormat({ decimals: option.value });
              }}
            >
              {option.label}
            </MenuItem>
          ))
        }
      </Dropdown>

      <Dropdown
        label="Fill colour"
        trigger={
          <span className="grid h-7 min-w-7 place-items-center rounded-md px-1.5 text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink">
            <FillColorIcon size="sm" />
          </span>
        }
      >
        {(close) =>
          FILLS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => {
                close();
                onFormat({ bg: option.value });
              }}
            >
              <Swatch color={option.value} />
              {option.label}
            </MenuItem>
          ))
        }
      </Dropdown>

      <Dropdown
        label="Text colour"
        trigger={
          <span className="grid h-7 min-w-7 place-items-center rounded-md px-1.5 text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink">
            <TextColorIcon size="sm" />
          </span>
        }
      >
        {(close) =>
          COLORS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => {
                close();
                onFormat({ color: option.value });
              }}
            >
              <Swatch color={option.value} />
              {option.label}
            </MenuItem>
          ))
        }
      </Dropdown>

      <ToolbarButton label="Clear formatting" disabled={!canEdit} onClick={onClearFormat}>
        <ClearFormatIcon size="sm" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton label="Total the selection" disabled={!canEdit} onClick={onSum}>
        <FormulaIcon size="sm" />
      </ToolbarButton>
      <ToolbarButton label={copied ? 'Copied' : 'Copy'} pressed={copied} onClick={copy}>
        <CopyIcon size="sm" />
      </ToolbarButton>
      <ToolbarButton label="Clear contents" disabled={!canEdit} onClick={onClear}>
        <TrashIcon size="sm" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton label="Add 50 rows" disabled={!canEdit} onClick={() => onGrow('rows')}>
        <AddRowsIcon size="sm" />
      </ToolbarButton>
      <ToolbarButton label="Add 10 columns" disabled={!canEdit} onClick={() => onGrow('cols')}>
        <AddColumnsIcon size="sm" />
      </ToolbarButton>
    </div>
  );
}
